// C.O.R.E. for Schools — shared design system for the platform chrome (navbar, pages, cards…).
// Tokens mirror the brand site (core-aiowl.org) and the BRAND constants used by the interactive lessons.
import React from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

export const BRAND = {
  navy900: '#07142d',
  navy800: '#0b1a3a',
  navy700: '#12213c',
  navy600: '#1b2d52',
  gold: '#cd9b50',
  goldLight: '#e2b878',
  goldPale: '#f3dcb4',
  ivory: '#f9f0e7',
  ivory2: '#f2e7da',
  ink: '#0b1224',
  inkSoft: '#3d4761',
  muted: 'rgba(249,240,231,0.68)',
  faint: 'rgba(249,240,231,0.45)',
  line: 'rgba(205,155,80,0.28)',
  lineSoft: 'rgba(205,155,80,0.16)',
  lineIvory: 'rgba(11,18,36,0.12)',
  success: '#5fbf8a',
  warning: '#e2b878',
  danger: '#e07a7a',
  font: "'Outfit', system-ui, -apple-system, sans-serif",
  serif: "'Cormorant Garamond', Georgia, serif",
  owl: '/brand/core-owl.png',
  bg: { classroom: '/brand/core-classroom.jpg', lab: '/brand/core-lab.jpg', school: '/brand/core-school.jpg' },
};

export const GOLD_GRADIENT = `linear-gradient(135deg, ${BRAND.goldLight}, ${BRAND.gold})`;
export const IVORY_GRADIENT_TEXT = {
  background: `linear-gradient(135deg, ${BRAND.ivory} 0%, ${BRAND.goldLight} 70%, ${BRAND.gold} 100%)`,
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
};

// Cast lookup for avatars / host chips (keys match the `host` / `who` values used by the lesson decks).
export const CAST = {
  trace: { name: 'Trace', role: 'Host', img: '/characters/cast/trace.png' },
  mina: { name: 'Mina', role: 'Best-practices expert', img: '/characters/cast/mina.png' },
  flash: { name: 'Dr. Flash', role: 'Inventor', img: '/characters/cast/flash.png' },
  prince: { name: 'Prince', role: 'Lab assistant', img: '/characters/cast/prince.png' },
  jason: { name: 'Jason', role: 'Villain', img: '/characters/cast/jason.png' },
  jackie: { name: 'Jackie', role: 'Villain', img: '/characters/cast/jackie.png' },
  cass: { name: 'Cass', role: 'Threat-hunter leader', img: '/characters/cast/cass.png' },
  juss: { name: 'Juss', role: 'Threat hunter', img: '/characters/cast/juss.png' },
  tim: { name: 'Tim', role: 'Student', img: '/characters/cast/tim.png' },
  ben: { name: 'Ben', role: 'Student', img: '/characters/cast/ben.png' },
  profz: { name: 'Professor Z', role: 'Scientist', img: '/characters/cast/profz.png' },
};

export const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] } },
};
export const stagger = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.06, delayChildren: 0.05 } },
};

/* ---------- layout ---------- */

// Full page wrapper. tone='dark' (students) draws a faint brand photo behind the content;
// tone='light' (admin) uses the ivory "paper" surface with the same navy chrome around it.
// `hero` = { img, pos, height } turns the backdrop into a lesson-style "scene": the picture is
// full-bleed at the top with the navy gradient anchored to the content (like the deck slides),
// a slow Ken Burns drift and the owl watermark.
export function Page({ bg = 'classroom', tone = 'dark', hero = null, children, style, maxWidth = 1240 }) {
  const dark = tone === 'dark';
  return (
    <div style={{ position: 'relative', minHeight: 'calc(100vh - 64px)', background: dark ? BRAND.navy900 : BRAND.ivory, fontFamily: BRAND.font, color: dark ? BRAND.ivory : BRAND.ink, overflow: 'hidden' }}>
      {dark && hero && (
        <div aria-hidden style={{ position: 'absolute', inset: 0, zIndex: 0, pointerEvents: 'none' }}>
          <div style={{ position: 'absolute', left: 0, right: 0, top: 0, height: hero.height || 640, overflow: 'hidden' }}>
            <AnimatePresence initial={false}>
              <motion.img key={hero.img} src={hero.img} alt=""
                initial={{ opacity: 0, scale: 1.08, x: 0 }}
                animate={{ opacity: hero.opacity ?? 0.62, scale: 1.16, x: -14 }}
                exit={{ opacity: 0 }}
                transition={{ opacity: { duration: 1.1, ease: 'easeInOut' }, scale: { duration: 28, ease: 'linear', repeat: Infinity, repeatType: 'reverse' }, x: { duration: 28, ease: 'linear', repeat: Infinity, repeatType: 'reverse' } }}
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: hero.pos || 'center 30%', display: 'block', transformOrigin: 'center' }} />
            </AnimatePresence>
            <div style={{ position: 'absolute', inset: 0, background: `linear-gradient(180deg, rgba(7,20,45,0.22) 0%, rgba(7,20,45,0.52) 48%, ${BRAND.navy900} 100%)` }} />
            <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg, rgba(7,20,45,0.82) 0%, rgba(7,20,45,0.35) 40%, rgba(7,20,45,0.1) 70%, rgba(7,20,45,0.5) 100%)' }} />
          </div>
          <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(1200px 500px at 85% 0%, rgba(205,155,80,0.14), transparent 60%)' }} />
          <img src={BRAND.owl} alt="" style={{ position: 'absolute', right: -60, bottom: -80, width: 460, opacity: 0.045, filter: 'grayscale(1) brightness(2)', transform: 'rotate(-8deg)' }} />
        </div>
      )}
      {dark && !hero && bg && (
        <div aria-hidden style={{ position: 'absolute', inset: 0, zIndex: 0, pointerEvents: 'none' }}>
          <img src={BRAND.bg[bg] || bg} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.16, display: 'block' }} />
          <div style={{ position: 'absolute', inset: 0, background: `linear-gradient(180deg, rgba(7,20,45,0.35) 0%, ${BRAND.navy900} 72%)` }} />
        </div>
      )}
      <div style={{ position: 'relative', zIndex: 1, maxWidth, margin: '0 auto', padding: 'clamp(22px,3vw,36px) clamp(18px,3.5vw,48px) 56px', ...style }}>
        {children}
      </div>
    </div>
  );
}

export function Kicker({ children, light = false, style }) {
  return <div style={{ fontWeight: 800, letterSpacing: '0.3em', fontSize: '0.74rem', textTransform: 'uppercase', color: light ? BRAND.gold : BRAND.goldLight, ...style }}>{children}</div>;
}

export function Serif({ children, light = false, style }) {
  return <p style={{ fontFamily: BRAND.serif, fontStyle: 'italic', fontSize: '1.3rem', lineHeight: 1.3, color: light ? BRAND.inkSoft : 'rgba(249,240,231,0.86)', margin: 0, ...style }}>{children}</p>;
}

export function PageHeader({ kicker, title, subtitle, right, light = false, style }) {
  return (
    <motion.div variants={fadeUp} initial="hidden" animate="visible" style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 20, flexWrap: 'wrap', marginBottom: 26, ...style }}>
      <div>
        {kicker && <Kicker light={light}>{kicker}</Kicker>}
        <h1 style={{ fontSize: 'clamp(1.9rem,3.2vw,2.5rem)', fontWeight: 900, lineHeight: 1.05, margin: '6px 0 6px', color: light ? BRAND.ink : BRAND.ivory }}>{title}</h1>
        {subtitle && <Serif light={light} style={{ fontSize: '1.2rem' }}>{subtitle}</Serif>}
      </div>
      {right && <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>{right}</div>}
    </motion.div>
  );
}

/* ---------- surfaces ---------- */

export const cardStyle = (light = false) => (light
  ? { background: '#fff', border: `1px solid ${BRAND.lineIvory}`, borderRadius: 20, boxShadow: '0 8px 26px rgba(11,18,36,0.06)', color: BRAND.ink }
  : { background: 'rgba(11,26,58,0.72)', border: `1px solid ${BRAND.line}`, borderRadius: 22, backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)', boxShadow: '0 18px 50px rgba(0,0,0,0.38)', color: BRAND.ivory });

export function Card({ light = false, children, style, hover = false, ...rest }) {
  const Comp = hover ? motion.div : 'div';
  const hoverProps = hover ? { whileHover: { y: -4, boxShadow: light ? '0 14px 34px rgba(11,18,36,0.10)' : '0 24px 60px rgba(0,0,0,0.5)' }, transition: { duration: 0.25 } } : {};
  return <Comp {...hoverProps} style={{ ...cardStyle(light), padding: 22, ...style }} {...rest}>{children}</Comp>;
}

export function Tag({ children, tone = 'gold', light = false, style }) {
  const tones = {
    gold: { bg: 'rgba(205,155,80,0.14)', bd: BRAND.line, fg: light ? BRAND.gold : BRAND.goldLight },
    solid: { bg: GOLD_GRADIENT, bd: 'transparent', fg: BRAND.navy900 },
    muted: { bg: light ? 'rgba(11,18,36,0.05)' : 'rgba(249,240,231,0.08)', bd: light ? BRAND.lineIvory : 'rgba(249,240,231,0.14)', fg: light ? BRAND.inkSoft : BRAND.muted },
    success: { bg: 'rgba(95,191,138,0.14)', bd: 'rgba(95,191,138,0.4)', fg: light ? '#2f8f5b' : '#8fd9ad' },
    danger: { bg: 'rgba(224,122,122,0.14)', bd: 'rgba(224,122,122,0.4)', fg: light ? '#b64747' : '#f0a0a0' },
  };
  const t = tones[tone] || tones.gold;
  return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 11px', borderRadius: 999, fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', background: t.bg, border: `1px solid ${t.bd}`, color: t.fg, whiteSpace: 'nowrap', ...style }}>{children}</span>;
}

export function ProgressBar({ value = 0, height = 6, style, light = false }) {
  return (
    <div style={{ height, borderRadius: height, background: light ? 'rgba(11,18,36,0.08)' : 'rgba(226,184,120,0.14)', overflow: 'hidden', ...style }}>
      <motion.div initial={{ width: 0 }} animate={{ width: `${Math.max(0, Math.min(100, value))}%` }} transition={{ duration: 0.7, ease: 'easeOut' }} style={{ height: '100%', borderRadius: height, background: `linear-gradient(90deg, ${BRAND.gold}, ${BRAND.goldLight})` }} />
    </div>
  );
}

export function Avatar({ cast, size = 36, style }) {
  const c = CAST[cast] || null;
  if (!c) return null;
  return <img src={c.img} alt={c.name} title={c.name} style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', objectPosition: 'top', border: `2px solid ${BRAND.gold}`, background: BRAND.navy700, flexShrink: 0, ...style }} />;
}

export function Initials({ name = '', size = 32, style }) {
  const txt = String(name).trim().split(/\s+/).map(w => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase() || '?';
  return <span style={{ width: size, height: size, borderRadius: '50%', background: GOLD_GRADIENT, color: BRAND.navy900, display: 'inline-grid', placeItems: 'center', fontWeight: 900, fontSize: size * 0.38, flexShrink: 0, ...style }}>{txt}</span>;
}

/* ---------- controls ---------- */

export const buttonStyle = (variant = 'gold', size = 'md', light = false) => {
  const pad = size === 'lg' ? '15px 32px' : size === 'sm' ? '7px 14px' : '11px 22px';
  const fs = size === 'lg' ? '1.02rem' : size === 'sm' ? '0.82rem' : '0.92rem';
  const base = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 9, padding: pad, borderRadius: 999, fontWeight: 800, fontSize: fs, fontFamily: BRAND.font, cursor: 'pointer', border: '1px solid transparent', textDecoration: 'none', lineHeight: 1.1, transition: 'transform .18s, box-shadow .18s, background .18s', whiteSpace: 'nowrap' };
  const v = {
    gold: { background: GOLD_GRADIENT, color: BRAND.navy900, boxShadow: '0 8px 26px rgba(205,155,80,0.32)' },
    outline: { background: 'transparent', color: light ? BRAND.gold : BRAND.goldLight, borderColor: BRAND.gold },
    ghost: { background: light ? 'rgba(11,18,36,0.05)' : 'rgba(249,240,231,0.08)', color: light ? BRAND.ink : BRAND.ivory, borderColor: light ? BRAND.lineIvory : 'rgba(249,240,231,0.14)' },
    navy: { background: BRAND.navy800, color: BRAND.ivory, borderColor: BRAND.line },
    danger: { background: 'rgba(224,122,122,0.14)', color: light ? '#b64747' : '#f0a0a0', borderColor: 'rgba(224,122,122,0.4)' },
  };
  return { ...base, ...(v[variant] || v.gold) };
};

export function Button({ variant = 'gold', size = 'md', light = false, to, href, children, style, disabled, ...rest }) {
  const st = { ...buttonStyle(variant, size, light), ...(disabled ? { opacity: 0.55, cursor: 'not-allowed' } : null), ...style };
  const hover = disabled ? {} : { whileHover: { scale: 1.03 }, whileTap: { scale: 0.97 } };
  if (to) return <motion.span {...hover} style={{ display: 'inline-flex' }}><Link to={to} style={st} {...rest}>{children}</Link></motion.span>;
  if (href) return <motion.a href={href} {...hover} style={st} {...rest}>{children}</motion.a>;
  return <motion.button type="button" {...hover} style={st} disabled={disabled} {...rest}>{children}</motion.button>;
}

export const inputStyle = (light = false) => ({
  width: '100%',
  padding: '12px 14px',
  fontSize: '0.95rem',
  fontFamily: BRAND.font,
  borderRadius: 12,
  border: `1px solid ${light ? BRAND.lineIvory : 'rgba(249,240,231,0.16)'}`,
  background: light ? '#fff' : 'rgba(7,20,45,0.6)',
  color: light ? BRAND.ink : BRAND.ivory,
  outline: 'none',
  boxSizing: 'border-box',
});

export const labelStyle = (light = false) => ({ display: 'block', fontSize: '0.74rem', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: light ? BRAND.gold : BRAND.goldLight, margin: '0 0 6px' });

export function Field({ label, light = false, children, style }) {
  return (
    <div style={{ marginBottom: 14, ...style }}>
      {label && <label style={labelStyle(light)}>{label}</label>}
      {children}
    </div>
  );
}

export function Segmented({ options, value, onChange, light = false, fill = false, style }) {
  return (
    <div style={{ display: fill ? 'flex' : 'inline-flex', width: fill ? '100%' : undefined, padding: 4, borderRadius: 999, background: light ? 'rgba(11,18,36,0.05)' : 'rgba(7,20,45,0.7)', border: `1px solid ${light ? BRAND.lineIvory : BRAND.line}`, gap: 2, ...style }}>
      {options.map(o => {
        const on = o.value === value;
        return (
          <button key={o.value} type="button" onClick={() => onChange(o.value)} style={{ flex: fill ? 1 : undefined, padding: '8px 16px', borderRadius: 999, border: 'none', cursor: 'pointer', fontFamily: BRAND.font, fontWeight: 800, fontSize: '0.86rem', background: on ? GOLD_GRADIENT : 'transparent', color: on ? BRAND.navy900 : (light ? BRAND.inkSoft : BRAND.muted), transition: 'all .2s' }}>
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export function EmptyState({ icon = '🦉', title, text, action, light = false }) {
  return (
    <Card light={light} style={{ textAlign: 'center', padding: '44px 24px' }}>
      <div style={{ fontSize: '2.6rem', marginBottom: 10 }}>{icon}</div>
      <p style={{ fontWeight: 800, fontSize: '1.15rem', margin: 0 }}>{title}</p>
      {text && <p style={{ color: light ? BRAND.inkSoft : BRAND.muted, marginTop: 8, maxWidth: 420, marginLeft: 'auto', marginRight: 'auto', lineHeight: 1.5 }}>{text}</p>}
      {action && <div style={{ marginTop: 18 }}>{action}</div>}
    </Card>
  );
}

export function Alert({ tone = 'danger', children, light = false, style }) {
  const c = tone === 'danger' ? { bg: 'rgba(224,122,122,0.12)', bd: 'rgba(224,122,122,0.45)', fg: light ? '#9c3b3b' : '#f3b1b1' } : { bg: 'rgba(205,155,80,0.12)', bd: BRAND.line, fg: light ? BRAND.ink : BRAND.ivory };
  return <div role="alert" style={{ padding: '12px 16px', borderRadius: 14, background: c.bg, border: `1px solid ${c.bd}`, color: c.fg, fontWeight: 600, fontSize: '0.92rem', marginBottom: 18, ...style }}>{children}</div>;
}

// Stat tile used on dashboard / stats / admin
export function Stat({ icon, value, label, light = false, children, style }) {
  return (
    <Card light={light} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '16px 18px', ...style }}>
      <span style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(205,155,80,0.14)', border: `1px solid ${BRAND.line}`, display: 'grid', placeItems: 'center', fontSize: '1.25rem', flexShrink: 0 }}>{icon}</span>
      <div style={{ minWidth: 0, flex: 1 }}>
        <b style={{ fontSize: '1.45rem', display: 'block', lineHeight: 1 }}>{value}</b>
        <span style={{ fontSize: '0.74rem', color: light ? BRAND.inkSoft : BRAND.muted, textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700 }}>{label}</span>
        {children}
      </div>
    </Card>
  );
}

// Pick a cover thumbnail + host from an interactive deck
export function deckMeta(lesson) {
  const slides = lesson?.interactiveSlides || [];
  const first = slides.find(s => s && s.img) || null;
  const cover = slides.find(s => s && s.host) || null;
  const chapters = new Set(slides.filter(s => s && s.chTitle).map(s => s.chTitle)).size;
  const badges = slides.filter(s => s && s.award).length;
  const missions = slides.filter(s => s && (s.sorter || s.branch || s.phone || s.passwordLab)).length;
  return { img: first?.img || null, host: cover?.host || null, slides: slides.length, chapters, badges, missions, isCore: slides.some(s => s && s.layout === 'scene') };
}
